/**
 * Software queries and mutations for TanStack Query (Vendor App)
 *
 * This module provides all the necessary queries and mutations for software version management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { queryKeys } from '../keys'
import { queryGet, queryPostFormData } from '../utils/request'

// Type definitions
export interface Software {
  id: number
  name: string
  description?: string
  is_active: boolean
}

export interface SoftwareVersion {
  id: number
  software_id: number
  version_code: number
  version_name: string
  size_bytes: number
  sha256_hash: string
  is_active: boolean
  created_at: string
  updated_at?: string
}

export interface SoftwareListParams {
  active_only?: boolean
}

export interface SoftwareVersionListParams {
  software_id?: number
  active_only?: boolean
  page?: number
  per_page?: number
}

export interface UploadVersionParams {
  software_id: number
  version_code: number
  version_name: string
  file: File
}

// Cache configuration
const CACHE_TIMES = {
  SOFTWARES: 10 * 60 * 1000, // 10 minutes
  VERSIONS: 2 * 60 * 1000, // 2 minutes
}

/**
 * QUERIES
 */

/**
 * Query hook to fetch list of softwares
 */
export const useSoftwaresQuery = (params: MaybeRefOrGetter<SoftwareListParams> = { active_only: true }) => {
  return useQuery({
    queryKey: computed(() => [...queryKeys.software(), 'list', toValue(params)]),
    queryFn: async (): Promise<Software[]> => {
      const queryParams = toValue(params)
      return queryGet<Software[]>('/v2/admin/softwares', { params: queryParams })
    },
    staleTime: CACHE_TIMES.SOFTWARES,
  })
}

/**
 * Query hook to fetch software versions
 */
export const useSoftwareVersionsQuery = (params: MaybeRefOrGetter<SoftwareVersionListParams>) => {
  return useQuery({
    queryKey: computed(() => queryKeys.softwareList(toValue(params))),
    queryFn: async (): Promise<SoftwareVersion[]> => {
      const queryParams = { active_only: true, ...toValue(params) }
      return queryGet<SoftwareVersion[]>('/v2/admin/software-versions', { params: queryParams })
    },
    staleTime: CACHE_TIMES.VERSIONS,
    enabled: computed(() => !!toValue(params)?.software_id),
  })
}

/**
 * MUTATIONS
 */

/**
 * Mutation hook to upload a new software version
 */
export const useUploadVersionMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: UploadVersionParams): Promise<SoftwareVersion> => {
      const formData = new FormData()
      formData.append('software_id', String(params.software_id))
      formData.append('version_code', String(params.version_code))
      formData.append('version_name', params.version_name)
      formData.append('file', params.file)

      return queryPostFormData<SoftwareVersion>('/v2/admin/software-versions', formData)
    },
    onSuccess: (_, variables) => {
      // Invalidate software versions list to refetch
      queryClient.invalidateQueries({
        queryKey: queryKeys.softwareList({ software_id: variables.software_id })
      })
    },
  })
}

/**
 * Hook that combines software queries and mutations for convenient usage
 */
export const useSoftwareOperations = () => {
  return {
    // Queries
    useSoftwaresQuery,
    useSoftwareVersionsQuery,

    // Mutations
    useUploadVersionMutation,
  }
}
